import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  keyframes, 
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import OptimizedImage from '../components/OptimizedImage';
import HuevoDeDragon from '../assets/medals2/Huevo_de_Dragon_v2.png';
import CriaDeDragon from '../assets/medals2/Cria_de_Dragon_v2.png';
import DragonJoven from '../assets/medals2/Dragon_Joven_v2.png';
import DragonDeFuego from '../assets/medals2/Dragon_de_Fuego_v2.png';
import DragonAstral from '../assets/medals2/Dragon_Astral_v2.png';
import DragonEterno from '../assets/medals2/Dragon_Eterno_v2.png';
import SectionTitle from '../components/SectionTitle';

// Definir keyframes para la animación de brillo original
const glowAnimation = keyframes`
  0% {
    filter: brightness(1) drop-shadow(0 0 0 rgba(255, 215, 0, 0));
  }
  50% {
    filter: brightness(1.2) drop-shadow(0 0 10px rgba(255, 215, 0, 0.5));
  }
  100% {
    filter: brightness(1) drop-shadow(0 0 0 rgba(255, 215, 0, 0));
  }
`;

// Definir keyframes para la animación de pulso
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

// Opción 1: Efecto de Brillo Directo (Glow Overlay)
const glowOverlayAnimation = keyframes`
  0% {
    box-shadow: 0 0 5px 2px rgba(255, 215, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 15px 5px rgba(255, 215, 0, 0.6);
  }
  100% {
    box-shadow: 0 0 5px 2px rgba(255, 215, 0, 0.3);
  }
`;

// Opción 2: Efecto de Partículas Brillantes
const particleAnimation = keyframes`
  0% {
    transform: translate(0, 0) scale(0);
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(1);
    opacity: 0;
  }
`;

// Opción 3: Efecto de Aura Pulsante
const auraAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.2;
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
`;

// Opción 4: Efecto de Brillo Interior
const innerGlowAnimation = keyframes`
  0% {
    filter: brightness(1) contrast(1);
  }
  50% {
    filter: brightness(1.3) contrast(1.1);
  }
  100% {
    filter: brightness(1) contrast(1);
  }
`;

// Opción 5: Efecto de Destello Intermitente
const flashAnimation = keyframes`
  0%, 100% {
    filter: brightness(1);
  }
  5%, 15% {
    filter: brightness(1.5);
  }
  20%, 100% {
    filter: brightness(1);
  }
`;

// Definir keyframes para la animación de brillo con gradiente
const gradientGlowAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

function HelpPage() {
  const [selectedSection, setSelectedSection] = useState('crear');

  const handleSectionClick = (section) => {
    setSelectedSection(section);
  };

  const ranks = [
    { 
      image: HuevoDeDragon, 
      label: 'Huevo de Dragón', 
      number: 1,
      glowColor: 'rgba(255, 215, 0, 0.35)',
      animationDuration: '3s'
    },
    { 
      image: CriaDeDragon, 
      label: 'Cría de Dragón', 
      number: 2,
      glowColor: 'rgba(255, 215, 0, 0.45)',
      animationDuration: '2.8s'
    },
    { 
      image: DragonJoven, 
      label: 'Dragón Joven', 
      number: 3,
      glowColor: 'rgba(255, 165, 0, 0.55)',
      animationDuration: '2.6s'
    },
    { 
      image: DragonDeFuego, 
      label: 'Dragón de Fuego', 
      number: 4,
      glowColor: 'rgba(255, 69, 0, 0.65)',
      animationDuration: '2.4s'
    },
    { 
      image: DragonAstral, 
      label: 'Dragón Astral', 
      number: 5,
      glowColor: 'rgba(138, 43, 226, 0.75)',
      animationDuration: '2.2s'
    },
    { 
      image: DragonEterno, 
      label: 'Dragón Eterno', 
      number: 6,
      glowColor: 'rgba(255, 0, 255, 0.85)',
      animationDuration: '2s'
    }
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2
      }}>
        <Typography 
          variant="h5" 
          fontWeight="bold"
          color="secondary"
        >
           Ayuda (En Progreso)
        </Typography>
      </Box>

      <Stack spacing={2}>
        {/* Sección de Partidas */}
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: 'background.paper' }}
          >
            <Typography variant="h6" color="primary">🎮 Partidas</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={4}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Crear Nueva Partida
                </Typography>
                <Stack spacing={1} sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    1. Selecciona "Nueva Partida" en el menú principal
                  </Typography>
                  <Typography variant="body2">
                    2. Configura los detalles:
                  </Typography>
                  <Stack spacing={0.5} sx={{ pl: 2 }}>
                    <Typography variant="body2">• Número de partidas a jugar</Typography>
                    <Typography variant="body2">• Modo de juego (1v1 o 2v2)</Typography>
                    <Typography variant="body2">• Tipo de partida (Amistoso)</Typography>
                    <Typography variant="body2">• Selecciona los jugadores para cada equipo</Typography>
                    <Typography variant="body2">• Asigna los mazos a cada jugador</Typography>
                    <Typography variant="body2">• Registra el resultado de las partidas</Typography>
                    <Typography variant="body2">• Opcionalmente, selecciona hasta 3 highlights de la partida</Typography>
                  </Stack>
                  <Typography variant="body2">
                    3. Sistema de aprobación:
                  </Typography>
                  <Stack spacing={0.5} sx={{ pl: 2 }}>
                    <Typography variant="body2">• La partida comienza en estado "Pendiente"</Typography>
                    <Typography variant="body2">• Todos los jugadores deben aprobar la partida</Typography>
                    <Typography variant="body2">• Una vez aprobada, se registra en estadísticas y se suman los puntos</Typography>
                  </Stack>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Estados de la Partida
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <WatchLaterIcon sx={{ color: 'warning.main', fontSize: '1.2rem', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Pendiente</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Esperando aprobación de todos los jugadores para registrar en estadísticas.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Aprobada</Typography>
                      <Typography variant="body2" color="text.secondary">
                        La partida ha sido aprobada por todos los jugadores y se ha registrado en estadísticas.
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <CloseIcon sx={{ color: 'error.main', fontSize: '1.2rem', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Rechazada</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Un jugador ha rechazado la partida. Puedes:
                      </Typography>
                      <Stack spacing={0.5} sx={{ pl: 2, mt: 0.5 }}>
                        <Typography variant="body2">• Aceptar el rechazo</Typography>
                        <Typography variant="body2">• Crear una nueva partida con los mismos datos</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          En ambos casos la partida se cancelará.
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <BlockIcon sx={{ color: 'grey.600', fontSize: '1.2rem', mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="bold">Cancelada</Typography>
                      <Typography variant="body2" color="text.secondary">
                        La partida ha sido cancelada y no se registrará en estadísticas.
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Proceso de Aprobación
                </Typography>
                <Stack spacing={1} sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    1. Después de jugar, la partida queda en estado "Pendiente"
                  </Typography>
                  <Typography variant="body2">
                    2. Cada jugador debe aprobar o rechazar la partida. Si el creador de la partida es también jugador, su aprobación ya está dada.
                  </Typography>
                  <Typography variant="body2">
                    3. Si todos aprueban:
                  </Typography>
                  <Stack spacing={0.5} sx={{ pl: 2 }}>
                    <Typography variant="body2">• La partida se registra en estadísticas</Typography>
                    <Typography variant="body2">• Los puntos se suman a los perfiles</Typography>
                  </Stack>
                  <Typography variant="body2">
                    4. Si alguien rechaza:
                  </Typography>
                  <Stack spacing={0.5} sx={{ pl: 2 }}>
                    <Typography variant="body2">• La partida se marca como "Rechazada"</Typography>
                    <Typography variant="body2">• Puedes crear una nueva con los mismos datos</Typography>
                    <Typography variant="body2">• O aceptar el rechazo. Ambas acciones cancelan la partida</Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Perfil */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: 'background.paper' }}
          >
            <Typography variant="h6" color="primary">👤 Perfil</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Tu Perfil Incluye
                </Typography>
                <Stack spacing={1} sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    • Estadísticas de juego:
                    - Partidas ganadas/perdidas
                    - Ratio de victorias
                    - Tiempo total jugado
                  </Typography>
                  <Typography variant="body2">
                    • Colección de mazos:
                    - Mazos creados
                    - Mazos favoritos
                    - Historial de uso
                  </Typography>
                  <Typography variant="body2">
                    • Historial de partidas:
                    - Últimas partidas jugadas
                    - Resultados detallados
                    - Oponentes frecuentes
                  </Typography>
                  <Typography variant="body2">
                    • Logros y Rangos:
                    - Rango actual
                    - Progreso hacia el siguiente rango
                    - Logros desbloqueados
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Sistema de Rangos */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: 'background.paper' }}
          >
            <Typography variant="h6" color="primary">🏆 Sistema de Rangos</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {ranks.map((rank) => (
                <Grid item xs={12} sm={6} md={4} key={rank.number}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.default',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }
                  }}>
                    <Typography variant="h6" sx={{ minWidth: '24px', color: 'primary.main', fontWeight: 'bold' }}>
                      {rank.number}
                    </Typography>
                    <Box sx={{ position: 'relative' }}>
                      <OptimizedImage
                        src={rank.image}
                        alt={rank.label}
                        width="80px"
                        height="80px"
                      />
                    </Box>
                    <Typography variant="body1">
                      {rank.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Sección de Preguntas Frecuentes */}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: 'background.paper' }}
          >
            <Typography variant="h6" color="primary">❓ Preguntas Frecuentes</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  ¿Qué son los Tiers?
                </Typography>
                <Typography variant="body2" sx={{ pl: 2 }}>
                  Los Tiers son niveles de dificultad y recompensa en las partidas. Cada Tier tiene diferentes requisitos y otorga distintas cantidades de puntos.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  ¿Qué es una Arena?
                </Typography>
                <Typography variant="body2" sx={{ pl: 2 }}>
                  Una Arena es un espacio de juego donde se desarrollan las partidas. Cada Arena puede tener reglas especiales y modificadores únicos.
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  ¿Cuántos puntos da cada Tier?
                </Typography>
                <Typography variant="body2" sx={{ pl: 2 }}>
                  Los puntos varían según el Tier:
                  • Tier 1: 100 puntos
                  • Tier 2: 200 puntos
                  • Tier 3: 300 puntos
                  • Tier 4: 500 puntos
                </Typography>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
}

export default HelpPage;